import React, { useState } from "react";
import {
  getSchoolFirm,
  upsertSchoolFirmProfile,
} from "app/services/management";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import { Helmet } from "react-helmet";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Input,
  MenuItem,
  Stack,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  useMediaQuery,
  Button,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import DialogHeader from "app/components/common/DialogHeader";
import { useFormik } from "formik";
import * as yup from "yup";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";

import { MenuProps, ValidateFileSize } from "app/utils/helper";
import { States, Distrct } from "app/config/administrativeConstants";
import { LoadingButton } from "@mui/lab";
import { ButtonWrapper, UploadBtn } from "app/modules/student/components";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";

const InfoRow = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: "0.875rem", display: "block" }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      color="primary.dark"
      sx={{ fontSize: "1.25rem", wordBreak: "break-word" }}
    >
      {value || "--"}
    </Typography>
  </Box>
);

const validationSchema = yup.object({
  firmName: yup.string().required("Firm Name Required"),
  firstName: yup.string().required("First name Required"),
  address: yup.string().required("address Required"),
  dist: yup.string().required("district Required"),
  pinCode: yup.string().required("pinCode Required"),
  state: yup.string().required("state Required"),
  description: yup.string().required("description Required"),
  phoneNumber: yup.string().required("Phone no. Required"),
  mobileNumber: yup.string().required("Mobile No. Required"),
  email: yup.string().required("Email Required"),
  website: yup.string().required("Website Required"),
  // gstNumber: yup.string().required("gstNumber Required"),
});

const FirmSchoolProfile = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [open, setOpen] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadTarget, setUploadTarget] = useState();
  const [stateIndex, setStateIndex] = useState(0);
  const [attachmentFile, setAttachmentFile] = useState(null);

  const formik = useFormik({
    initialValues: {
      profilePic: "",
      coverPhoto: "",
      firmName: "",
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      mobileNumber: "",
      email: "",
      website: "",
      address: "",
      dist: "",
      pinCode: "",
      state: "",
      gstNumber: "",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const updateFirmProfile = {
        profilePic: values.profilePic,
        coverPhoto: values.coverPhoto,
        firmName: values.firmName,
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        phoneNumber: String(values.phoneNumber),
        mobileNumber: String(values.mobileNumber),
        website: values?.website,
        email: values.email,
        schoolFirmAddress: {
          address: values.address,
          dist: values.dist,
          pinCode: values.pinCode,
          state: values.state,
        },
        gstNumber: values.gstNumber,
        description: values.description,
      };
      mutate(updateFirmProfile);
    },
    enableReinitialize: true,
  });
  const { mutate, isLoading: updatingSchoolProfile } = useMutation(
    upsertSchoolFirmProfile,

    {
      onSuccess: ({ data }) => {
        toast.success(data?.message);
        setOpen(false);
        getSchoolFirmrefetch();
      },
      onError: (error) => {
        toast.error(error.response.data?.message);
      },
    }
  );
  const { handleUploadImage } = useFolderWiseFileUploader();
  const handleUploadFile = async () => {
    if (attachmentFile) {
      const { valid, message } = ValidateFileSize(attachmentFile, {
        validateImage: true,
        imageLimit: 0.5 * 1048576, // 0.5 MB
      });
      if (!valid) {
        toast.error(message);
        return;
      }
      let attachmentUrl = await handleUploadImage(
        true,
        attachmentFile,
        "withSession",
        "SCHOOL_FIRM",
        "FIRM_PROFILE"
      );
      if (uploadTarget === "cover") {
        formik.setValues((prev) => ({
          ...prev,
          coverPhoto: attachmentUrl.fileURL,
        }));
      }
      if (uploadTarget === "profile") {
        formik.setValues((prev) => ({
          ...prev,
          profilePic: attachmentUrl.fileURL,
        }));
      }
      setOpenUploadDialog(false);
      setAttachmentFile(null);
    }
  };
  const {
    data: getSchoolFirmData,
    isLoading: loadingSchoolProfile,
    refetch: getSchoolFirmrefetch,
    isFetching,
  } = useQuery({
    queryKey: ["getSchoolFirm"],
    queryFn: () => getSchoolFirm(),
    onSuccess: ({ data }) => {
      formik.setValues((prev) => ({
        ...prev,
        profilePic: data?.obj?.profilePic,
        coverPhoto: data?.obj?.coverPhoto,
        firmName: data?.obj?.firmName,
        description: data?.obj?.description,
        firstName: data?.obj?.firstName,
        middleName: data?.obj?.middleName,
        lastName: data?.obj?.lastName,
        address: data?.obj?.schoolFirmAddress?.address,
        dist: data?.obj?.schoolFirmAddress?.dist,
        pinCode: data?.obj?.schoolFirmAddress?.pinCode,
        state: data?.obj?.schoolFirmAddress?.state,
        affiliatedTo: data?.obj?.affiliatedTo,
        affiliationNumber: data?.obj?.affiliationNumber,
        educationType: data?.obj?.educationType,
        gstNumber: data?.obj?.gstNumber,
        schoolRegisterDate: data?.obj?.schoolRegisterDate,
        email: data?.obj?.email,
        website: data?.obj?.website,
        phoneNumber: data?.obj?.phoneNumber,
        mobileNumber: data?.obj?.mobileNumber,
        schoolGstNumber: data?.obj?.gstNumber,
        licenseNumber: data?.obj?.ekalRefNumber,
        licenseDate: data?.obj?.verifiedOn,
        verifiedOn: data?.obj?.verifiedOn,
        affiliationUpTo: data?.obj?.affiliationUpTo,
        signatureForIdCard: data?.obj?.signatureForIdCard,
      }));
    },
    onError: (data) => {
      toast.error(data?.response?.data?.message);
    },
  });
  let schoolProfile = getSchoolFirmData?.data?.obj;
  return (
    <>
      {/* upload photo */}
      <Dialog
        maxWidth="xs"
        fullWidth
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
      >
        <DialogTitle>Upload {uploadTarget} photo</DialogTitle>
        <DialogContent>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setAttachmentFile(e.target.files[0])}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            color="secondary"
            size="medium"
            variant="contained"
            onClick={() => {
              handleUploadFile();
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogHeader title="Edit Profile" handleClose={() => setOpen(false)} />
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Box
              component="img"
              src={formik.values.coverPhoto || "/banner-placeholder.png"}
              alt="Cover Photo"
              sx={{
                width: "100%",
                height: 120,
                borderRadius: 2,
                objectFit: "cover",
                mb: 1,
              }}
            />
            <Box
              onClick={() => {
                setOpenUploadDialog(true);
                setUploadTarget("cover");
              }}
              sx={{
                border: "1px solid",
                borderColor: "secondary.main",
                borderRadius: 2,
                p: 1,
                textAlign: "center",
                cursor: "pointer",
                mb: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography color="secondary" variant="body2">
                Upload Cover Photo
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src={formik.values.profilePic || "/image-placeholder.jpeg"}
                alt="Profile Pic"
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
              />
              <Box
                onClick={() => {
                  setOpenUploadDialog(true);
                  setUploadTarget("profile");
                }}
                sx={{
                  border: "1px solid",
                  borderColor: "secondary.main",
                  borderRadius: 2,
                  p: 1,
                  textAlign: "center",
                  cursor: "pointer",
                  flexGrow: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography color="secondary" variant="body2">
                  Upload Logo
                </Typography>
              </Box>
            </Stack>
            <Grid container spacing={2}>
              {/* 1st */}
              <Grid item xs={12}>
                <TextFieldLabel title="Firm Name" required />
                <CustomTextField
                  size="small"
                  fullWidth
                  id="firmName"
                  name="firmName"
                  value={formik.values.firmName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.firmName && Boolean(formik.errors.firmName)
                  }
                  helperText={formik.touched.firmName && formik.errors.firmName}
                />
              </Grid>
              {/* 2nd */}
              <Grid item xs={4}>
                <TextFieldLabel title="Owner’s First Name" required />

                <CustomTextField
                  size="small"
                  fullWidth
                  id="firstName"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextFieldLabel title="Owner’s Middle Name" />
                <CustomTextField
                  size="small"
                  fullWidth
                  id="middleName"
                  name="middleName"
                  value={formik.values.middleName}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextFieldLabel title="Owner’s Last Name" />
                <CustomTextField
                  size="small"
                  fullWidth
                  id="lastName"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                />
              </Grid>

              {/* 3rd */}
              <Grid item xs={6}>
                <TextFieldLabel title="Firm Phone Number :" required />
                <CustomTextField
                  size="small"
                  fullWidth
                  id="phoneNumber"
                  name="phoneNumber"
                  // type="number"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextFieldLabel title="Mobile Number :" required />
                <CustomTextField
                  size="small"
                  fullWidth
                  id="mobileNumber"
                  name="mobileNumber"
                  type="number"
                  value={formik.values.mobileNumber}
                  onChange={(e) => {
                    if (String(e.target.value).length < 11) {
                      formik.handleChange(e);
                    }
                  }}
                />
              </Grid>

              {/* 4th */}
              <Grid item xs={6}>
                <TextFieldLabel title="Firm Email Address :" required />
                <CustomTextField
                  size="small"
                  fullWidth
                  placeholder="type  email"
                  id="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextFieldLabel title="Firm Website :" required />
                <CustomTextField
                  placeholder="type  website"
                  size="small"
                  fullWidth
                  id="website"
                  name="website"
                  value={formik.values.website}
                  onChange={formik.handleChange}
                />
              </Grid>

              {/* 5th */}
              <Grid item xs={12}>
                <TextFieldLabel title="Firm’s Address :" required />
                <CustomTextField
                  size="small"
                  fullWidth
                  id="address"
                  name="address"
                  placeholder="type school address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>

              {/* 6th */}

              <Grid item xs={4}>
                <TextFieldLabel title="State" required />
                <CustomSelectBox
                  displayEmpty
                  fullWidth
                  size="small"
                  id="state"
                  required
                  value={formik.values.state}
                  name="state"
                  onChange={formik.handleChange}
                  MenuProps={MenuProps}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                >
                  <MenuItem disabled value="">
                    Select
                  </MenuItem>
                  {States.map((state, index) => (
                    <MenuItem
                      key={state}
                      value={state}
                      onClick={() => {
                        setStateIndex(index);
                      }}
                    >
                      {state}
                    </MenuItem>
                  ))}
                </CustomSelectBox>
              </Grid>
              <Grid item xs={4}>
                <TextFieldLabel title="District" required />
                <CustomSelectBox
                  size="small"
                  fullWidth
                  displayEmpty
                  id="dist"
                  required
                  value={formik.values.dist}
                  name="dist"
                  onChange={formik.handleChange}
                  MenuProps={MenuProps}
                  error={formik.touched.dist && Boolean(formik.errors.dist)}
                  helperText={formik.touched.dist && formik.errors.dist}
                >
                  <MenuItem disabled value="">
                    Select
                  </MenuItem>
                  {Distrct[States.indexOf(String(formik.values.state)) + 1].map(
                    (district) => (
                      <MenuItem key={district} value={district}>
                        {district}
                      </MenuItem>
                    )
                  )}
                </CustomSelectBox>
              </Grid>
              <Grid item xs={4}>
                <TextFieldLabel title="Pincode" required />
                <CustomTextField
                  fullWidth
                  placeholder="type pin code"
                  size="small"
                  id="pinCode"
                  name="pinCode"
                  value={formik.values.pinCode}
                  onChange={formik.handleChange}
                  inputProps={{
                    maxLength: 6,
                  }}
                  error={
                    formik.touched.pinCode && Boolean(formik.errors.pinCode)
                  }
                  helperText={formik.touched.pinCode && formik.errors.pinCode}
                />
              </Grid>

              {/* 7th */}

              <Grid item xs={6}>
                <TextFieldLabel title="Firm GST Number :" />
                <CustomTextField
                  size="small"
                  fullWidth
                  placeholder="type school registration number"
                  id="gstNumber"
                  name="gstNumber"
                  value={formik.values.gstNumber}
                  onChange={formik.handleChange}
                />
              </Grid>

              {/* 8th */}
              <Grid item xs={12}>
                <TextFieldLabel title="Description :" required />
                <CustomTextField
                  size="small"
                  placeholder="description"
                  fullWidth
                  id="description"
                  name="description"
                  // type="number"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextFieldLabel title="Note :" />
                <Typography sx={{ fontSize: "15px" }}>
                  After updating All data will be updated in existing Invoices
                  Also.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              loading={updatingSchoolProfile}
              color="secondary"
              variant="contained"
              type="submit"
              size="large"
              sx={{ fontSize: "capitalize" }}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
      <Helmet>
        <title>School Firm Profile</title>
      </Helmet>

      <Box sx={{ mb: isMobile ? 10 : 0 }}>
        {(loadingSchoolProfile || isFetching) && (
          <Stack direction="row" justifyContent="center" sx={{ py: 2 }}>
            <CircularProgress />
          </Stack>
        )}

        <Card elevation={2} sx={{ overflow: "hidden", borderRadius: 4 }}>
          {/* Cover Photo Section */}
          <CardMedia
            component="img"
            height="180"
            image={schoolProfile?.coverPhoto || "/banner-placeholder.png"}
            alt="Firm Cover"
            sx={{ objectFit: "cover" }}
          />
          {/* Logo and Edit Button */}
          <Box sx={{ position: "relative", mt: -5, textAlign: "center" }}>
            <Avatar
              src={schoolProfile?.profilePic || "/image-placeholder.jpeg"}
              alt="Firm Logo"
              sx={{
                width: 80,
                height: 80,
                border: "4px solid white",
                boxShadow: 2,
                mx: "auto",
                bgcolor: "background.paper",
              }}
            />
            <IconButton
              onClick={() => setOpen(true)}
              size="small"
              sx={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(10px)",
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                width: 28,
                height: 28,
              }}
            >
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <CardContent sx={{ pt: 1, pb: 2 }}>
            {/* Firm Name */}
            <Typography
              variant="h5"
              fontWeight={600}
              textAlign="center"
              gutterBottom
            >
              {schoolProfile?.firmName}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Information Grid */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={3}>
                <InfoRow
                  label="Owner Name"
                  value={`${schoolProfile?.firstName || ""} ${
                    schoolProfile?.middleName || ""
                  } ${schoolProfile?.lastName || ""}`.trim()}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <InfoRow
                  label="Mobile Number"
                  value={schoolProfile?.mobileNumber}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <InfoRow
                  label="Firm Phone Number"
                  value={schoolProfile?.phoneNumber}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <InfoRow label="Email Address" value={schoolProfile?.email} />
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <InfoRow label="Website" value={schoolProfile?.website} />
              </Grid>

              <Grid item xs={12} sm={8} md={6}>
                <InfoRow
                  label="Firm's Address"
                  value={
                    schoolProfile?.schoolFirmAddress
                      ? `${schoolProfile.schoolFirmAddress.address}, ${schoolProfile.schoolFirmAddress.dist}, ${schoolProfile.schoolFirmAddress.state} (${schoolProfile.schoolFirmAddress.pinCode})`
                      : null
                  }
                />
              </Grid>

              <Grid item xs={6} sm={4} md={3}>
                <InfoRow label="GST Number" value={schoolProfile?.gstNumber} />
              </Grid>

              <Grid item xs={12}>
                <InfoRow
                  label="Description"
                  value={schoolProfile?.description}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default FirmSchoolProfile;
